import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline';
import AdminHeader from '../../../shared/components/AdminHeader';
import { getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { PASSING_GRADE, getGradeBand } from '../../../shared/constant/grading';
import { TERMS } from '../../../shared/constant/terms';
import { showAlert, showSuccessToast } from '../../../shared/utils/dialogs';
import { getOwnAdvisory } from '../../Teacher/services/teacherService';
import {
  getAdvisoryAttendance,
  getAdvisoryGrades,
  getAdvisorySecretary,
  getAdvisoryStudents,
  getAdvisorySubjects,
  removeClassSecretary,
  setClassSecretary,
} from '../services/advisoryService';
import StudentRecordModal from './StudentRecordModal';

const GRADES_TAB = 'Grades';
const ATTENDANCE_TAB = 'Attendance';
const TABS = [GRADES_TAB, ATTENDANCE_TAB];

const formatPercent = (value) => (value == null ? '—' : `${Number(value).toFixed(2)}%`);

const studentName = (s) => `${s.lastname}, ${s.firstname} ${s.middlename ? `${s.middlename} ` : ''}`;

// The name column stays put while the subject columns scroll sideways.
const STICKY_HEAD = 'px-4 py-3 font-medium sticky left-0 z-20 bg-[#4ea6d0]';
// The pinned cell needs an opaque background, so on hover it uses the solid colour of the row's
// translucent hover tint (#4ea6d0 at 20% over white) instead of letting the tint show through.
const STICKY_CELL = 'px-4 py-3 font-medium text-[#0b4a71] sticky left-0 bg-white group-hover:bg-[#dcedf6]';

function AdvisoryPage() {
  const [advisory, setAdvisory] = useState(null);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [baseLoading, setBaseLoading] = useState(true);
  const [error, setError] = useState('');

  const [tab, setTab] = useState(GRADES_TAB);
  const [term, setTerm] = useState(String(TERMS[0]));
  const [subjectFilter, setSubjectFilter] = useState('');
  const [viewIndex, setViewIndex] = useState(null);
  const [grades, setGrades] = useState({});
  const [attendance, setAttendance] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  const [secretaryId, setSecretaryId] = useState('');
  const [secretarySaving, setSecretarySaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    getOwnAdvisory()
      .then(async (result) => {
        if (!result) return;
        const [studentRows, subjectRows] = await Promise.all([
          getAdvisoryStudents(result),
          getAdvisorySubjects(result),
        ]);
        if (!mounted) return;
        setAdvisory(result);
        setStudents(studentRows);
        setSubjects(subjectRows);
        const currentSecretary = await getAdvisorySecretary(studentRows.map((s) => s.id));
        if (mounted) setSecretaryId(currentSecretary ?? '');
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Failed to load your advisory class');
      })
      .finally(() => {
        if (mounted) setBaseLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleSecretaryChange = async (e) => {
    const newId = e.target.value;
    setSecretarySaving(true);
    try {
      if (!newId) {
        if (secretaryId) await removeClassSecretary(secretaryId);
      } else {
        await setClassSecretary(newId, secretaryId);
      }
      setSecretaryId(newId);
      showSuccessToast('Class secretary updated');
    } catch (err) {
      showAlert({ text: err.message || 'Failed to update class secretary' });
    } finally {
      setSecretarySaving(false);
    }
  };

  const studentIds = useMemo(() => students.map((s) => s.id), [students]);

  useEffect(() => {
    if (studentIds.length === 0) return undefined;
    let mounted = true;
    setDataLoading(true);
    setError('');

    // The subject filter only applies to attendance; grades already show every subject.
    const load =
      tab === GRADES_TAB
        ? () => getAdvisoryGrades(studentIds, Number(term))
        : () => getAdvisoryAttendance(studentIds, Number(term), subjectFilter);
    const apply = tab === GRADES_TAB ? setGrades : setAttendance;

    load()
      .then((result) => {
        if (mounted) apply(result);
      })
      .catch((err) => {
        if (mounted) setError(err.message || `Failed to load ${tab.toLowerCase()}`);
      })
      .finally(() => {
        if (mounted) setDataLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [studentIds, tab, term, subjectFilter]);

  const renderGrades = () => (
    <table className="w-full text-sm">
      <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
        <tr>
          <th className={STICKY_HEAD}>Name</th>
          {subjects.map((subject) => (
            <th key={subject.id} className="px-4 py-3 font-medium">{subject.name}</th>
          ))}
          <th className="px-4 py-3 font-medium">General Average</th>
          <th className="px-4 py-3 font-medium">Remarks</th>
          <th className="px-4 py-3 font-medium text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {students.map((student, studentIndex) => {
          const termGrades = subjects
            .map((subject) => grades[`${student.id}|${subject.id}`])
            .filter((grade) => grade != null);
          const average = termGrades.length
            ? termGrades.reduce((sum, grade) => sum + grade, 0) / termGrades.length
            : null;
          const band = getGradeBand(average);

          return (
            <tr key={student.id} className="group hover:bg-[#4ea6d0]/20">
              <td className={STICKY_CELL}>{studentName(student)}</td>
              {subjects.map((subject) => {
                const grade = grades[`${student.id}|${subject.id}`];
                return (
                  <td
                    key={subject.id}
                    className={`px-4 py-3 ${grade != null && grade < PASSING_GRADE ? 'text-red-600 font-medium' : 'text-gray-600'}`}
                  >
                    {grade ?? '—'}
                  </td>
                );
              })}
              <td className="px-4 py-3 font-medium text-[#0b4a71]">{average == null ? '—' : average.toFixed(2)}</td>
              <td className="px-4 py-3">
                {band ? (
                  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${band.tone}`}>
                    {band.remark}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setViewIndex(studentIndex)}
                    aria-label={`View record of ${student.lastname}, ${student.firstname}`}
                    className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-[#0b4a71]"
                  >
                    <EyeIcon className="h-4 w-4" />
                    View
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderAttendance = () => (
    <table className="w-full text-sm">
      <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
        <tr>
          <th className={STICKY_HEAD}>Name</th>
          <th className="px-4 py-3 font-medium">Present</th>
          <th className="px-4 py-3 font-medium">Late</th>
          <th className="px-4 py-3 font-medium">Absent</th>
          <th className="px-4 py-3 font-medium">Excused</th>
          <th className="px-4 py-3 font-medium">Attendance</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {students.map((student) => {
          const row = attendance[student.id];
          return (
            <tr key={student.id} className="group hover:bg-[#4ea6d0]/20">
              <td className={STICKY_CELL}>{studentName(student)}</td>
              <td className="px-4 py-3 text-gray-600">{row?.present ?? '—'}</td>
              <td className="px-4 py-3 text-gray-600">{row?.late ?? '—'}</td>
              <td className="px-4 py-3 text-gray-600">{row?.absent ?? '—'}</td>
              <td className="px-4 py-3 text-gray-600">{row?.excused ?? '—'}</td>
              <td className="px-4 py-3 font-medium text-[#0b4a71]">{formatPercent(row?.attendance_pct)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <AdminHeader />

      <main className="flex-1 min-h-0 w-full p-4 lg:w-4/5 lg:mx-auto flex flex-col space-y-4">
        <div className="shrink-0 flex items-center gap-2">
          <Link to="/dashboard" aria-label="Back to dashboard" className="text-[#0b4a71] hover:text-[#3f6d8e]">
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-semibold text-[#0b4a71]">Advisory</h1>
        </div>

        {baseLoading ? (
          <p className="w-full text-center text-gray-500 py-12">Loading…</p>
        ) : !advisory ? (
          <p className="w-full text-center text-gray-500 py-12">
            {error || 'You have no advisory class yet. Ask an admin to assign one to you.'}
          </p>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col space-y-3">
            <p className="shrink-0 text-md text-gray-500">
              {getGradeLevelLabel(advisory.grade_level)} - {advisory.section} · {students.length} student
              {students.length === 1 ? '' : 's'}
            </p>

            <div className="shrink-0 flex items-center gap-2">
              <label htmlFor="classSecretary" className="text-sm font-medium text-gray-700">Class Secretary</label>
              <select
                id="classSecretary"
                value={secretaryId}
                onChange={handleSecretaryChange}
                disabled={secretarySaving || students.length === 0}
                className="rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0] disabled:opacity-50"
              >
                <option value="">None</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>{studentName(s)}</option>
                ))}
              </select>
              {secretarySaving && <span className="text-xs text-gray-500">Saving…</span>}
            </div>

            <div className="shrink-0 flex flex-wrap gap-2 items-center">
              {TABS.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setTab(name)}
                  className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition ${
                    tab === name
                      ? 'bg-[#4ea6d0] border-[#4ea6d0] text-white'
                      : 'border-gray-300 text-gray-600 hover:border-[#4ea6d0] hover:text-[#0b4a71]'
                  }`}
                >
                  {name}
                </button>
              ))}

              <select
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                aria-label="Term"
                className="rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              >
                {TERMS.map((t) => (
                  <option key={t} value={t}>Term {t}</option>
                ))}
              </select>

              {tab === ATTENDANCE_TAB && subjects.length > 0 && (
                <select
                  value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                  aria-label="Filter by subject"
                  className="rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                >
                  <option value="">All Subjects</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>{subject.name}</option>
                  ))}
                </select>
              )}
            </div>

            <p className="shrink-0 text-xs text-gray-500">
              {tab === GRADES_TAB
                ? 'Term grade in each subject taught to this section. The general average counts only the subjects graded so far.'
                : `${subjectFilter ? `In ${subjects.find((s) => s.id === subjectFilter)?.name ?? 'this subject'}` : 'Across all subjects'}. Only present days count as attended; late is shown separately and excused days are left out.`}
            </p>

            {error && (
              <p className="shrink-0 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
            )}

            <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
              {dataLoading ? (
                <p className="text-center text-gray-500 py-12">Loading…</p>
              ) : students.length === 0 ? (
                <p className="text-center text-gray-500 py-12">No students found in your advisory section.</p>
              ) : tab === GRADES_TAB && subjects.length === 0 ? (
                <p className="text-center text-gray-500 py-12">
                  No subjects are assigned to this section yet.
                </p>
              ) : (
                <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
                  {tab === GRADES_TAB ? renderGrades() : renderAttendance()}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {viewIndex !== null && (
        <StudentRecordModal
          students={students}
          index={viewIndex}
          subjects={subjects}
          onIndexChange={setViewIndex}
          onClose={() => setViewIndex(null)}
        />
      )}
    </div>
  );
}

export default AdvisoryPage;
